/**
 * <nb-tabs> — filtered category panels.
 *
 * Progressive enhancement: the markup ships as a tablist with one panel per
 * group and every panel visible. This class hides all but the active panel, so
 * without JavaScript the page still shows every card rather than an empty grid.
 */
class NbTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabs.length < 2) return;

    this.tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => this.select(i));
      tab.addEventListener('keydown', (event) => this.onKeydown(event, i));
    });

    this.select(0, { focus: false });
  }

  onKeydown(event, index) {
    const last = this.tabs.length - 1;
    let next = null;

    switch (event.key) {
      case 'ArrowRight':
        next = index === last ? 0 : index + 1;
        break;
      case 'ArrowLeft':
        next = index === 0 ? last : index - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.select(next);
  }

  select(index, { focus = true } = {}) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
      // Roving tabindex: only the active tab is reachable by Tab, and the
      // arrow keys move between them from there.
      tab.tabIndex = active ? 0 : -1;
    });

    this.panels.forEach((panel, i) => {
      panel.hidden = i !== index;
    });

    if (focus) this.tabs[index].focus();

    this.reveal(this.panels[index]);
  }

  /**
   * Cards in a hidden panel never intersect the viewport, so they are still
   * sitting in their pre-reveal state when the panel is shown. Adding the class
   * here plays the transition on the newly visible cards.
   */
  reveal(panel) {
    if (!panel) return;
    requestAnimationFrame(() => {
      panel.querySelectorAll('[data-nb-reveal]').forEach((el) => {
        el.classList.add('nb-revealed');
      });
    });
  }
}

if (!customElements.get('nb-tabs')) {
  customElements.define('nb-tabs', NbTabs);
}
